/* eslint-disable @typescript-eslint/no-explicit-any */
import { io } from "socket.io-client"
import { create } from "zustand"
import { useUserStore } from "../user"
import { useProcessCreationLoadingStore } from "../process-creation-loading"
import {
  QUERY_KEYS,
  ROUTER_PATHS,
  SOCKET_EXCEPTIONS,
  SOCKET_RESPONSES,
  LANG_KEYS,
  DISCOVERED_TRANSLATION_KEYS,
} from "@/shared/constants"
import { queryClient } from "@/shared/api/query-client"
import type { GetLibraryContentDtoRes } from "@/shared/api/generated"
import { useModalStore } from "../modal"
import { Modal } from "@/shared/types"
import { ListenUntilModal } from "@/widgets"
import { toast } from "sonner"
import { NavigationService } from "../navigation-service"
import { activePlayer, chunkedAudioEngine } from "../audio-engine"
import { useOptionsStore } from "../options"
import {
  contentControllerGetItemById,
  contentControllerShouldIReconnectToProcessSocket,
} from "@/shared/api/generated"
import { autoTranslate } from "../localization"

interface ProcessSocketStore {
  connectToProcessSocket: () => Promise<void>
  emitToProcessSocket: (event: string, data?: any) => Promise<void>
}

// The same item can sit in several cached library pages at once (the "All"
// tab and the "From extension" tab are separate queries), so patch every
// page that contains it instead of hunting for a single one.
const updateAudioTimeInCacheById = (
  contentId: string,
  totalAudioTime: number
) => {
  queryClient.setQueriesData<GetLibraryContentDtoRes>(
    { queryKey: ["LIBRARY_CONTENT"] },
    old => {
      if (!old || !old.data.some(item => item.id === contentId)) return old
      return {
        ...old,
        data: old.data.map(item =>
          item.id === contentId ? { ...item, audioTime: totalAudioTime } : item
        ),
      }
    }
  )
}

// Prefix invalidation: every tab / filter / page of the library refreshes,
// not only the page the user is looking at.
const invalidateActiveAndArchivedPagesAndTempContent = async () => {
  await queryClient.invalidateQueries({ queryKey: ["LIBRARY_CONTENT"] })
  await queryClient.invalidateQueries({ queryKey: ["ARCHIVED_CONTENT"] })
  await queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.TEMP_CONTENT,
  })
}

const SOCKET_URL = import.meta.env.VITE_API_URL + "/process"

export const processSocket = io(SOCKET_URL, {
  autoConnect: false,
  forceNew: true,
  auth: cb => {
    cb({ token: useUserStore.getState().access_token })
  },
})

const disconnectIfProcessingIsIdle = async () => {
  try {
    const hasActiveTask =
      await contentControllerShouldIReconnectToProcessSocket()
    if (!hasActiveTask) processSocket.disconnect()
  } catch {
    // Keep the socket if the status check failed; a later terminal event or
    // normal app teardown can safely retry/close it.
  }
}

const CONNECT_TIMEOUT_MS = 15000

export const useProcessSocketStore = create<ProcessSocketStore>()((_, get) => ({
  connectToProcessSocket: async () => {
    // A pending _connectingPromise is only ever meant to live for the
    // duration of one connection attempt (bounded by CONNECT_TIMEOUT_MS
    // below, which always clears it via .finally()). If we ever see one
    // that's older than that, something left it dangling - treat it as
    // abandoned rather than await it forever, or every future connect
    // attempt silently rides along on a promise that will never settle.
    const pendingSince = (processSocket as any)._connectingPromiseStartedAt
    if (
      (processSocket as any)._connectingPromise &&
      pendingSince &&
      Date.now() - pendingSince > CONNECT_TIMEOUT_MS
    ) {
      console.error(
        "[process-socket] discarding a stale connectingPromise that never settled"
      )
      ;(processSocket as any)._connectingPromise = null
    }

    // Prevent concurrent connection attempts
    if ((processSocket as any)._connectingPromise) {
      return (processSocket as any)._connectingPromise
    }

    const doConnect = async (): Promise<void> => {
      if (processSocket.connected) {
        return
      }

      // Remove all previous listeners to prevent duplicates on reconnect
      processSocket.removeAllListeners()
      processSocket.offAny()

      processSocket.on("disconnect", reason => {
        // This used to close the processing loader unconditionally on ANY
        // disconnect, including a transient blip totally unrelated to
        // completion (a network hiccup, or the reconnect churn below) - the
        // loader would vanish mid-process while content was still actually
        // being generated server-side. The real completion/error paths
        // already close it themselves (CONTENT_FIRST_CHUNK_COMPLETED,
        // CONTENT_EXCEPTION, etc. below) - only close it here as a fallback
        // once the server confirms nothing is actually still running.
        void contentControllerShouldIReconnectToProcessSocket()
          .then(hasActiveTask => {
            if (!hasActiveTask) {
              useProcessCreationLoadingStore.getState().closeAppLoading()
            }
            // The server also uses "io server disconnect" as a deliberate
            // goodbye: once a task fully finishes, it explicitly disconnects
            // that client's socket. Reconnecting unconditionally here used
            // to reconnect right after a completed task for no reason, only
            // to have the very next idle-check immediately disconnect it
            // again. Only reconnect if something is actually still active -
            // a real server restart/redeploy (the other case that produces
            // this reason) still gets us back online correctly.
            if (reason === "io server disconnect" && hasActiveTask) {
              processSocket.connect()
            }
          })
          .catch(() => {
            // Status check itself failed - err on the side of reconnecting,
            // leave the loader as-is rather than guess.
            if (reason === "io server disconnect") processSocket.connect()
          })
      })

      processSocket.on(
        SOCKET_RESPONSES.CONTENT_EXCEPTION,
        async ({ data }: any) => {
          if (!data) return
          useProcessCreationLoadingStore.getState().closeAppLoading()
          invalidateActiveAndArchivedPagesAndTempContent()
          void disconnectIfProcessingIsIdle()

          if (
            data === SOCKET_EXCEPTIONS.DAILY_SECONDS_LIMIT ||
            data === SOCKET_EXCEPTIONS.DAILY_SYMBOLS_LIMIT
          ) {
            useModalStore
              .getState()
              .openModal(Modal.LISTEN_UNTIL, <ListenUntilModal />)
            return
          }

          if (data === SOCKET_EXCEPTIONS.GUEST_CONTENT_LIMIT) {
            toast.error(
              autoTranslate(DISCOVERED_TRANSLATION_KEYS.GUEST_LIMIT_REACHED),
              {
                description: autoTranslate(
                  DISCOVERED_TRANSLATION_KEYS.CREATE_ACCOUNT_TO_CONTINUE
                ),
              }
            )
            NavigationService.navigate(ROUTER_PATHS.PROFILE_CONNECT_EMAIL)
            return
          }

          toast.error(data)
        }
      )

      processSocket.on(SOCKET_RESPONSES.CLIENT_PASSED_INFO, async () => {
        useProcessCreationLoadingStore.getState().setInfoPassed("passed")
        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TEMP_CONTENT,
        })
      })

      processSocket.on(
        SOCKET_RESPONSES.CONTENT_TEXT_LENGTH,
        async ({ data }: any) => {
          useProcessCreationLoadingStore.getState().setLoadingLength(data)
        }
      )

      processSocket.on(SOCKET_RESPONSES.ALREADY_PROCESSING, () => {
        useProcessCreationLoadingStore.getState().closeAppLoading()
        alert(
          `${autoTranslate(LANG_KEYS.ALREADY_PROCESSING)}\n${autoTranslate(
            LANG_KEYS.PLEASE_WAIT_FOR_THE_CURRENT_PROCESS_TO_COMPLETE
          )}`
        )
      })

      processSocket.on(
        SOCKET_RESPONSES.CONTENT_FIRST_CHUNK_COMPLETED,
        async ({ data }: any) => {
          const res = JSON.parse(data) as {
            contentId: string
          }
          useProcessCreationLoadingStore.getState().closeAppLoading()
          NavigationService.navigate(
            ROUTER_PATHS.APP_LIBRARY_DETAILS.replace(":id", res.contentId)
          )

          useOptionsStore.getState().invalidateOptions()
          invalidateActiveAndArchivedPagesAndTempContent()
        }
      )

      processSocket.on(
        SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED,
        async ({ data }: any) => {
          const res = JSON.parse(data) as {
            contentId: string
            audioChunkIdx: number
            totalAudioTime: number
          }
          useProcessCreationLoadingStore.getState().closeAppLoading()
          // Notify chunked audio engine about the new chunk
          chunkedAudioEngine.onNewChunkAvailable(res.contentId)
          updateAudioTimeInCacheById(res.contentId, res.totalAudioTime)
        }
      )

      processSocket.on(
        SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED,
        async ({ data }: any) => {
          const res = JSON.parse(data) as {
            contentId: string
            audioChunkIdx: number
            ok: boolean
          }
          // Refresh wordsReady flags so the "highlighting is generating"
          // state clears; the viewers refetch page words on this event.
          chunkedAudioEngine.onChunkWordsAvailable(res.contentId)
        }
      )

      processSocket.on(
        SOCKET_RESPONSES.CONTENT_CREATION_FINISHED,
        async ({ data }: any) => {
          try {
            const parsed = JSON.parse(data) as { contentId: string }
            const contentId = parsed.contentId

            // Always refetch and update query cache
            const freshItem = await contentControllerGetItemById(contentId)
            queryClient.setQueryData(
              QUERY_KEYS.CONTENT_BY_ID(contentId),
              freshItem
            )
            invalidateActiveAndArchivedPagesAndTempContent()

            // If user is currently listening to this content → switch to full audio
            if (activePlayer.id === contentId) {
              const currentPos = activePlayer.currentPosition
              const pos = currentPos > 0 ? currentPos : 0

              if (activePlayer.isPlaying) {
                await activePlayer.play(freshItem, {
                  force: true,
                  startPosition: pos,
                })
              } else {
                await activePlayer.load(freshItem, {
                  force: true,
                  startPosition: pos,
                })
              }
            }
          } catch (e) {
            console.error("CONTENT_CREATION_FINISHED handler error", e)
          } finally {
            void disconnectIfProcessingIsIdle()
          }
        }
      )

      processSocket.on(SOCKET_RESPONSES.EMPTY_IMAGE_OCR, async () => {
        useProcessCreationLoadingStore.getState().closeAppLoading()
        void disconnectIfProcessingIsIdle()
        alert(
          "We could not detect any text in your image" +
            "\n" +
            "Please ensure the photo clearly shows the text and try uploading it again"
        )
      })

      processSocket.connect()

      // This loop used to have no bound at all: if the connection attempt
      // stalled (bad network, DNS hiccup, whatever), this promise would
      // hang forever with no error, no log, nothing - and every caller
      // awaits this before doing anything (creating content, sending a
      // socket message), so the whole flow (including the upload loading
      // state) would silently freeze with zero feedback and no way to
      // recover except leaving the page.
      const startedConnectingAt = Date.now()
      while (!processSocket.connected) {
        if (Date.now() - startedConnectingAt > CONNECT_TIMEOUT_MS) {
          throw new Error(
            `Process socket did not connect within ${CONNECT_TIMEOUT_MS}ms`
          )
        }
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    ;(processSocket as any)._connectingPromiseStartedAt = Date.now()
    const promise = doConnect()
      .catch(e => {
        console.error("[process-socket] connect failed", e)
        throw e
      })
      .finally(() => {
        ;(processSocket as any)._connectingPromise = null
      })

    ;(processSocket as any)._connectingPromise = promise
    return promise
  },

  emitToProcessSocket: async (event: string, data: any) => {
    if (!processSocket.connected) {
      await get().connectToProcessSocket()
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    processSocket.emit(event, data ?? undefined)
  },
}))
